import { getFormattedName } from 'pages/AffordaHousehold/utils';
import moment from 'moment';
import { CREDIT_CRIMINAL_CHECK_PATHS } from 'constants/paths';

export const CREDIT_AND_CRIMINAL_CHECK_SECTION_NAMES = {
  MIDDLE_NAMES_AND_SUFFIXES: 'middleNamesAndSuffixes',
  MOVE_IN_PREFERENCES: 'moveInPreferences',
  CONSENT: 'consent',
  SUMMARY: 'summary',
};

export const MIDDLE_NAMES_AND_SUFFIXES_FIELD_NAMES = {
  haveMiddleNameOrSuffix: 'haveMiddleNameOrSuffix',
  middleName: 'middleName',
  suffix: 'suffix',
};

export const MOVE_IN_PREFERENCES_FIELD_NAMES = {
  desiredMoveInDate: 'desiredMoveInDate',
  preferredFloorPlan: 'preferredFloorPlan',
};

export const PATH_TO_SECTION_NAMES_MAP = {
  [CREDIT_CRIMINAL_CHECK_PATHS.MIDDLE_NAMES_AND_SUFFIXES]:
    CREDIT_AND_CRIMINAL_CHECK_SECTION_NAMES.MIDDLE_NAMES_AND_SUFFIXES,
  [CREDIT_CRIMINAL_CHECK_PATHS.MOVE_IN_PREFERENCES]:
    CREDIT_AND_CRIMINAL_CHECK_SECTION_NAMES.MOVE_IN_PREFERENCES,
  [CREDIT_CRIMINAL_CHECK_PATHS.CONSENT]:
    CREDIT_AND_CRIMINAL_CHECK_SECTION_NAMES.CONSENT,
  [CREDIT_CRIMINAL_CHECK_PATHS.SUMMARY]:
    CREDIT_AND_CRIMINAL_CHECK_SECTION_NAMES.SUMMARY,
};

export const CREDIT_AND_CRIMINAL_CHECK_PATHS_TO_FIELD_NAMES_MAP = {
  [CREDIT_CRIMINAL_CHECK_PATHS.MIDDLE_NAMES_AND_SUFFIXES]:
    MIDDLE_NAMES_AND_SUFFIXES_FIELD_NAMES,
  [CREDIT_CRIMINAL_CHECK_PATHS.MOVE_IN_PREFERENCES]:
    MOVE_IN_PREFERENCES_FIELD_NAMES,
};

const BASE_FIELD_PROPS = {
  GridProps: {
    xs: 12,
  },
};
export const MIDDLE_NAMES_AND_SUFFIXES_FIELDS = {
  HAVE_MIDDLE_NAME_OR_SUFFIX: ({ member, members, intl }) => ({
    name: `${member.id}.${MIDDLE_NAMES_AND_SUFFIXES_FIELD_NAMES.haveMiddleNameOrSuffix}`,
    label: intl.formatMessage(
      {
        id: 'afforda.creditAndCriminalCheck.middleNamesAndSuffixes.haveMiddleNameOrSuffix.label',
      },
      { name: getFormattedName(member, members, intl) },
    ),
    variant: 'buttonGroup',
    multiselect: true,
    options: [
      {
        label: intl.formatMessage({
          id: 'afforda.creditAndCriminalCheck.middleNamesAndSuffixes.middleName.label',
        }),
        value: 'middleName',
      },
      {
        label: intl.formatMessage({
          id: 'afforda.creditAndCriminalCheck.middleNamesAndSuffixes.suffix.label',
        }),
        value: 'suffix',
      },
    ],
    ...BASE_FIELD_PROPS,
  }),
  MIDDLE_NAME: ({ member, intl }) => ({
    name: `${member.id}.${MIDDLE_NAMES_AND_SUFFIXES_FIELD_NAMES.middleName}`,
    label: intl.formatMessage({
      id: 'afforda.creditAndCriminalCheck.middleNamesAndSuffixes.middleName.label',
    }),
    placeholder: intl.formatMessage({
      id: 'afforda.creditAndCriminalCheck.middleNamesAndSuffixes.middleName.label',
    }),
    variant: 'text',
    rules: {
      required: true,
      validate: (value) => {
        const isValid = /^[A-Za-z\s]+$/.test(value);
        if (!isValid) {
          return intl.formatMessage({
            id: 'app.error.numericNotAllowed',
          });
        }
        return isValid;
      },
    },
    hidden: ({ getValues }) =>
      !getValues(
        `${member.id}.${MIDDLE_NAMES_AND_SUFFIXES_FIELD_NAMES.haveMiddleNameOrSuffix}`,
      ).includes(MIDDLE_NAMES_AND_SUFFIXES_FIELD_NAMES.middleName),
    watchFields: [
      `${member.id}.${MIDDLE_NAMES_AND_SUFFIXES_FIELD_NAMES.haveMiddleNameOrSuffix}`,
    ],
    GridProps: {
      xs: 12,
      sm: 6,
    },
  }),
  SUFFIX: ({ member, intl, options }) => ({
    name: `${member.id}.${MIDDLE_NAMES_AND_SUFFIXES_FIELD_NAMES.suffix}`,
    label: intl.formatMessage({
      id: 'afforda.creditAndCriminalCheck.middleNamesAndSuffixes.suffix.label',
    }),
    placeholder: intl.formatMessage({ id: 'app.placeholder.select' }),
    variant: 'singleSelect',
    options,
    rules: {
      required: true,
    },
    hidden: ({ getValues }) =>
      !getValues(
        `${member.id}.${MIDDLE_NAMES_AND_SUFFIXES_FIELD_NAMES.haveMiddleNameOrSuffix}`,
      ).includes(MIDDLE_NAMES_AND_SUFFIXES_FIELD_NAMES.suffix),
    watchFields: [
      `${member.id}.${MIDDLE_NAMES_AND_SUFFIXES_FIELD_NAMES.haveMiddleNameOrSuffix}`,
    ],
    GridProps: {
      xs: 12,
      sm: 6,
    },
  }),
};

export const MOVE_IN_PREFERENCES_FIELDS = {
  DESIRED_MOVE_IN_DATE: ({ intl }) => ({
    name: MOVE_IN_PREFERENCES_FIELD_NAMES.desiredMoveInDate,
    label: intl.formatMessage({
      id: 'afforda.creditAndCriminalCheck.moveInPreferences.desiredMoveInDate.label',
    }),
    variant: 'date',
    disablePast: true,
    minDate: moment(),
    maxDate: moment().add(1, 'year'),
    rules: {
      required: true,
      validate: (value) => {
        const today = moment();
        const oneYearFromToday = moment(today).add(1, 'year');
        const isValid = moment(value).isBetween(
          today,
          oneYearFromToday,
          'day',
          [],
        );

        if (!isValid) {
          return intl.formatMessage({
            id: 'app.error.withinAYearFromToday',
          });
        }
        return isValid;
      },
    },
    GridProps: {
      xs: 12,
    },
  }),
  PREFERRED_FLOOR_PLAN: ({ intl, options }) => ({
    name: MOVE_IN_PREFERENCES_FIELD_NAMES.preferredFloorPlan,
    label: intl.formatMessage({
      id: 'afforda.creditAndCriminalCheck.moveInPreferences.preferredFloorPlan.label',
    }),
    variant: 'buttonGroup',
    options,
    direction: 'row',
    flexWrap: 'wrap',
    sx: {
      '& > button': {
        width: { xs: 'calc(50% - 4px)' },
      },
    },
    rules: {
      required: true,
    },
    GridProps: {
      xs: 12,
    },
    hidden: !options?.length,
  }),
};
